import { useState, useEffect, useCallback, useRef } from "react";
import {
  useSensors,
  useSensor,
  PointerSensor,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import type { KanbanColumn, TaskListItem, TaskStatus } from "../types";
import { useReorderTasksMutation } from "../api";

interface UseKanbanDndProps {
  projectId: number;
  columns: KanbanColumn[];
  onReorderError?: () => void;
  onColumnsChange?: (columns: KanbanColumn[]) => void;
}

function findTaskColumn(cols: KanbanColumn[], taskId: number): KanbanColumn | undefined {
  return cols.find((col) => col.tasks.some((t) => t.id === taskId));
}

export function useKanbanDnd({ projectId, columns, onReorderError, onColumnsChange }: UseKanbanDndProps) {
  const [localColumns, setLocalColumns] = useState<KanbanColumn[]>(columns);
  const [activeTask, setActiveTask] = useState<TaskListItem | null>(null);
  const [reorderTasks] = useReorderTasksMutation();
  const serverColumnsRef = useRef(columns);
  const isDraggingRef = useRef(false);

  const [prevColumns, setPrevColumns] = useState(columns);
  if (prevColumns !== columns) {
    setPrevColumns(columns);
    if (!isDraggingRef.current) {
      setLocalColumns(columns);
    }
  }

  useEffect(() => {
    if (!isDraggingRef.current) {
      serverColumnsRef.current = columns;
    }
  }, [columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    isDraggingRef.current = true;
    const taskId = Number(event.active.id);
    setLocalColumns((current) => {
      for (const col of current) {
        const task = col.tasks.find((t) => t.id === taskId);
        if (task) {
          setActiveTask(task);
          break;
        }
      }
      return current;
    });
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = Number(active.id);
    const overId = String(over.id);

    setLocalColumns((prev) => {
      const sourceColumn = findTaskColumn(prev, activeId);
      if (!sourceColumn) return prev;

      const isOverColumn = prev.some((col) => col.status === overId);
      const targetColumnStatus = isOverColumn
        ? overId
        : findTaskColumn(prev, Number(overId))?.status;

      if (!targetColumnStatus || sourceColumn.status === targetColumnStatus) return prev;

      const newColumns = prev.map((col) => ({ ...col, tasks: [...col.tasks] }));
      const srcCol = newColumns.find((c) => c.status === sourceColumn.status)!;
      const destCol = newColumns.find((c) => c.status === targetColumnStatus)!;

      const taskIndex = srcCol.tasks.findIndex((t) => t.id === activeId);
      if (taskIndex === -1) return prev;

      const [movedTask] = srcCol.tasks.splice(taskIndex, 1);
      const updatedTask = { ...movedTask, status: destCol.status as TaskStatus };

      if (isOverColumn) {
        destCol.tasks.push(updatedTask);
      } else {
        const overIndex = destCol.tasks.findIndex((t) => t.id === Number(overId));
        destCol.tasks.splice(overIndex >= 0 ? overIndex : destCol.tasks.length, 0, updatedTask);
      }

      return newColumns;
    });
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveTask(null);
      isDraggingRef.current = false;

      if (!over) {
        setLocalColumns(serverColumnsRef.current);
        onColumnsChange?.(serverColumnsRef.current);
        return;
      }

      const activeId = Number(active.id);
      const overId = over.id;

      let nextColumns = localColumns;
      const column = findTaskColumn(localColumns, activeId);

      if (column && column.tasks.some((t) => t.id === Number(overId))) {
        const oldIndex = column.tasks.findIndex((t) => t.id === activeId);
        const newIndex = column.tasks.findIndex((t) => t.id === Number(overId));

        if (oldIndex !== newIndex) {
          nextColumns = localColumns.map((col) => {
            if (col.status !== column.status) return col;
            return { ...col, tasks: arrayMove(col.tasks, oldIndex, newIndex) };
          });
          setLocalColumns(nextColumns);
        }
      }

      const originalMap = new Map<number, { status: string; position: number }>();
      for (const col of serverColumnsRef.current) {
        for (let i = 0; i < col.tasks.length; i++) {
          originalMap.set(col.tasks[i].id, { status: col.status, position: i });
        }
      }

      const updates: { id: number; status: TaskStatus; position: number }[] = [];
      for (const col of nextColumns) {
        for (let i = 0; i < col.tasks.length; i++) {
          const task = col.tasks[i];
          const original = originalMap.get(task.id);
          if (!original || original.status !== col.status || original.position !== i) {
            updates.push({ id: task.id, status: col.status as TaskStatus, position: i });
          }
        }
      }

      onColumnsChange?.(nextColumns);

      if (updates.length > 0) {
        reorderTasks({ projectId, data: { updates } })
          .unwrap()
          .then(() => {
            serverColumnsRef.current = nextColumns;
          })
          .catch(() => {
            setLocalColumns(serverColumnsRef.current);
            onColumnsChange?.(serverColumnsRef.current);
            onReorderError?.();
          });
      }
    },
    [localColumns, projectId, reorderTasks, onReorderError, onColumnsChange]
  );

  return {
    localColumns,
    activeTask,
    sensors,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
  };
}
